import { useEffect, useMemo, useRef, useState } from 'react';
import { PortalShell } from '@/portal/components/PortalShell';
import { Ico } from '@/portal/components/Icons';
import { AvailabilityBadge, ProductImage, ProductPeek } from '@/portal/components/ProductPeek';
import { ProductFilters, useProductFilters, applyProductFilters } from '@/portal/components/ProductFilters';
import { usePortalData } from '@/portal/data/PortalDataContext';
import { rangesOf } from '@/portal/data/product';
import type { Portfolio, PortalProduct } from '@/portal/api/types';

/* Per-card portfolio picker — shows all portfolios with checkmarks. */
function PortfolioPickerButton({
  productCode,
  portfolios,
  onToggle,
}: {
  productCode: string;
  portfolios: Portfolio[];
  onToggle: (portfolioId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const inAny = portfolios.some((pf) => pf.item_codes.includes(productCode));

  return (
    <div className="pl-pf-pick" ref={ref}>
      <button
        className={'pl-prod-add' + (inAny ? ' added' : '')}
        title={inAny ? 'Manage portfolios' : 'Add to portfolio'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {inAny ? <Ico.check width={16} height={16} /> : <Ico.plus width={16} height={16} />}
      </button>

      {open && (
        <div className="pl-pf-menu" onClick={(e) => e.stopPropagation()}>
          <div className="pl-pf-menu-head">Add to portfolio</div>
          {portfolios.length === 0 ? (
            <div className="pl-pf-empty">No portfolios yet — create one first.</div>
          ) : (
            portfolios.map((pf) => {
              const isIn = pf.item_codes.includes(productCode);
              return (
                <button
                  key={pf.id}
                  className={'pl-pf-opt' + (isIn ? ' on' : '')}
                  onClick={() => {
                    onToggle(pf.id);
                    /* keep dropdown open so user can multi-select */
                  }}
                >
                  <span className="pl-pf-dot" />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pf.title}
                  </span>
                  <span className="pl-pf-check" aria-hidden="true">✓</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export function CollectionPage() {
  const { collection, collectionLoading, collectionError, portfolios, setPortfolioItems } = usePortalData();
  const filters = useProductFilters();
  const [peek, setPeek] = useState<PortalProduct | null>(null);

  const visible = useMemo(() => applyProductFilters(collection, filters), [collection, filters]);
  const ranges = useMemo(() => rangesOf(visible), [visible]);
  const byRange = useMemo(() => {
    const map = new Map<string, PortalProduct[]>();
    visible.forEach((p) => {
      const arr = map.get(p.rangeKey) ?? [];
      arr.push(p);
      map.set(p.rangeKey, arr);
    });
    return map;
  }, [visible]);

  const toggleInPortfolio = async (portfolioId: string, code: string) => {
    const pf = portfolios.find((p) => p.id === portfolioId);
    if (!pf) return;
    const has = pf.item_codes.includes(code);
    const next = has ? pf.item_codes.filter((c) => c !== code) : [...pf.item_codes, code];
    await setPortfolioItems(pf.id, next);
  };

  return (
    <PortalShell title="My Collection" wide>

      {/* ── Sticky filter strip ──────────────────────────────── */}
      <div className="pl-coll-sticky">
        <div className="pl-search">
          <Ico.search width={16} height={16} />
          <input
            placeholder="Search products…"
            value={filters.q}
            onChange={(e) => filters.setQ(e.target.value)}
          />
          {filters.q && (
            <button
              style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, color: 'var(--ink-faint)', lineHeight: 1 }}
              onClick={() => filters.setQ('')}
              aria-label="Clear search"
            >
              <Ico.x width={14} height={14} />
            </button>
          )}
        </div>

        <div className="pl-collection-filters" style={{ marginBottom: 0 }}>
          <ProductFilters state={filters} source={collection} matchCount={visible.length} hideFooter />
        </div>

        <div className="pl-coll-meta">
          <span>{visible.length} products · {ranges.length} ranges</span>
          {filters.hasFilters && (
            <button className="pl-btn ghost sm" onClick={filters.clear}>Clear filters</button>
          )}
        </div>
      </div>

      {/* ── Product grid ─────────────────────────────────────── */}
      {collectionLoading && <div className="pl-empty">Loading your collection…</div>}
      {collectionError && <div className="pl-empty">{collectionError}</div>}

      {!collectionLoading &&
        ranges.map((r) => {
          const items = byRange.get(r.key) ?? [];
          return (
            <div key={r.key} style={{ marginBottom: 30 }}>
              <h3 style={{ fontSize: 16, marginBottom: 14 }}>
                {r.label}{' '}
                <span style={{ color: 'var(--ink-faint)', fontWeight: 300 }}>· {items.length}</span>
              </h3>
              <div className="pl-prod-grid">
                {items.map((p) => (
                  <div className="pl-prod" key={p.id}>
                    <PortfolioPickerButton
                      productCode={p.code}
                      portfolios={portfolios}
                      onToggle={(pfId) => void toggleInPortfolio(pfId, p.code)}
                    />
                    <div
                      className="pl-prod-tap"
                      role="button"
                      tabIndex={0}
                      onClick={() => setPeek(p)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setPeek(p)}
                      aria-label={`View ${p.name}`}
                    >
                      <div className="pl-prod-img">
                        <ProductImage src={p.img} alt={p.name} loading="lazy" />
                      </div>
                      <div className="pl-prod-body">
                        <div className="nm">{p.name}</div>
                        <div className="cd">{p.code}</div>
                        <AvailabilityBadge availability={p.availability} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

      {!collectionLoading && !collectionError && visible.length === 0 && (
        <div className="pl-empty">
          No products match.{' '}
          {collection.length === 0 && 'Your account has no ranges enabled yet — ask your account manager.'}
        </div>
      )}

      {peek && (
        <ProductPeek
          product={peek}
          added={portfolios.some((pf) => pf.item_codes.includes(peek.code))}
          onClose={() => setPeek(null)}
        />
      )}
    </PortalShell>
  );
}
